/* =============================================================
   FINAL NotificationSystem.jsx – 100% Story Compliant (Offline)
   ============================================================= */
import React, { useState, useMemo } from "react";

import {
  Modal,
  Input,
  Table,
  Tag,
  Space,
  Select,
  DatePicker,
  Badge,
  Empty,
  Typography,
} from "antd";
import { SearchOutlined, SendOutlined } from "@ant-design/icons";
import moment from "moment";
import { useQuery } from "@apollo/client";
import { GET_LIST_OF_COHORTS, GET_ADMIN_USERS_FOR_ROLES, GET_CAMPAIGN_LIST } from "../../services/userAPI";
import { useLocation } from "react-router-dom";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title } = Typography;

// ====================== RICH MOCK DATA ======================
const mockUsers = [
  { id: 1, name: "Amit Sharma", city: "Hyderabad", branch: "Banjara Hills", designation: "Relationship Manager", performance: "HIGH", tenure: "3+ yrs", appUsage: "High" },
  { id: 2, name: "Priya Reddy", city: "Hyderabad", branch: "Jubilee Hills", designation: "Relationship Manager", performance: "MEDIUM", tenure: "1-3 yrs", appUsage: "Medium" },
  { id: 3, name: "Rahul Kumar", city: "Port Blair", branch: "Aberdeen Bazaar", designation: "Relationship Manager", performance: "LOW", tenure: "<1 yr", appUsage: "Low" },
  { id: 4, name: "Sneha Mehta", city: "Vijayawada", branch: "Benz Circle", designation: "Senior RM", performance: "HIGH", tenure: "3+ yrs", appUsage: "High" },
  { id: 5, name: "Vikram Singh", city: "Hyderabad", branch: "Banjara Hills", designation: "Relationship Manager", performance: "HIGH", tenure: "3+ yrs", appUsage: "High" },
  { id: 6, name: "Ananya Das", city: "Vijayawada", branch: "MG Road", designation: "Relationship Manager", performance: "MEDIUM", tenure: "1-3 yrs", appUsage: "Medium" },
  { id: 7, name: "Rohan Patel", city: "Mumbai", branch: "Bandra West", designation: "Wealth Manager", performance: "HIGH", tenure: "5+ yrs", appUsage: "High" },
];

// ====================== MAIN COMPONENT ======================
const NotificationSystem = ({ visible, onCancel }) => {
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [senderFilter, setSenderFilter] = useState([]);
  const [readStatus, setReadStatus] = useState("all");
  const [campaignType, setCampaignType] = useState([]);
  const [appUsageFilter, setAppUsageFilter] = useState([]);
  const [tenureFilter, setTenureFilter] = useState([]);

  const location = useLocation();

  const roleId = location.state?.roleId;
  const accountId = location.state?.accountId;

  // Cascading Filters
  const [cityFilter, setCityFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [designationFilter, setDesignationFilter] = useState("");
  const [performanceFilter, setPerformanceFilter] = useState("");

  // Derived Options
  const cities = useMemo(() => [...new Set(mockUsers.map(u => u.city))].sort(), []);
  const branches = useMemo(() => {
    if (!cityFilter) return [];
    return [...new Set(mockUsers.filter(u => u.city === cityFilter).map(u => u.branch))].sort();
  }, [cityFilter]);
  const designations = useMemo(() => {
    let list = mockUsers;
    if (cityFilter) list = list.filter(u => u.city === cityFilter);
    if (branchFilter) list = list.filter(u => u.branch === branchFilter);
    return [...new Set(list.map(u => u.designation))].sort();
  }, [cityFilter, branchFilter]);

  const performanceMap = { HIGH: "Top Performer", MEDIUM: "Above Average", LOW: "Needs Improvement" };
  const tenureOptions = ["<1 yr", "1-3 yrs", "3+ yrs", "5+ yrs"];
  const appUsageOptions = ["High", "Medium", "Low"];

  const { data: adminsData, loading: adminsLoading } = useQuery(
    GET_ADMIN_USERS_FOR_ROLES,
    {
      variables: {
        roleName: "clan crew",
        jobroleId: roleId,
        filterByName: "",
      },
      fetchPolicy: "network-only",
    }
  );

  const senders = useMemo(
    () =>
      (adminsData?.getAdminUsersForRoles?.data || []).map((u) => ({
        id: u.id,
        name: `${u.firstName || ""} ${u.surname || ""}`.trim() || u.email || "",
      })),
    [adminsData]
  );

  const { data: cohortsData, loading: cohortsLoading } = useQuery(GET_LIST_OF_COHORTS, {
    variables: { accountId: null, jobroleId: null },
    fetchPolicy: "network-only",
  });

  const cohorts = useMemo(
    () => cohortsData?.getListOfCohorts || [],
    [cohortsData]
  );

  const fromDate = useMemo(
    () =>
      dateRange && dateRange.length === 2
        ? dateRange[0].startOf("day").toISOString()
        : null,
    [dateRange]
  );

  const toDate = useMemo(
    () =>
      dateRange && dateRange.length === 2
        ? dateRange[1].endOf("day").toISOString()
        : null,
    [dateRange]
  );

  const selectedCohortId = campaignType.length > 0 ? campaignType[0] : null;
  const selectedSenderId = senderFilter.length > 0 ? senderFilter[0] : null;
  const numericRoleId = roleId ? parseInt(roleId, 10) : null;

  const { data: campaignData, loading: campaignsLoading } = useQuery(
    GET_CAMPAIGN_LIST,
    {
      variables: {
        jobroleId: numericRoleId,
        fromDate,
        toDate,
        createdBy: selectedSenderId,
        cohortId: selectedCohortId,
      },
      fetchPolicy: "network-only",
      skip: !numericRoleId,
    }
  );

  const campaigns = useMemo(
    () =>
      (campaignData?.getCampaignList || []).map((c) => ({
        id: c.id,
        title: c.template?.title || "",
        description: c.template?.description || "",
        createdAt: c.createdAt,
        cohortName: c.cohort?.cohortName || "",
        cohortFilters: c.cohort?.filters || null,
        senderName: c.createdBy?.name || "",
      })),
    [campaignData]
  );

  const filteredNotifications = campaigns;

  const columns = [
    { title: "Title", dataIndex: "title", render: (t) => <strong>{t}</strong> },
    { title: "Message", dataIndex: "description", width: 320, ellipsis: true },
    {
      title: "Cohort Name",
      dataIndex: "cohortName",
      render: (name) => name || "-",
    },
    {
      title: "Filters",
      render: (_, r) => {
        const f = r.cohortFilters || {};
        const lines = [];
        if (f.city) lines.push({ label: "City", value: f.city });
        if (f.state) lines.push({ label: "State", value: f.state });
        if (f.designation) lines.push({ label: "Designation", value: f.designation });
        if (f.performanceBand) lines.push({ label: "Performance Band", value: f.performanceBand });

        if (lines.length === 0) return "-";

        return (
          <div>
            {lines.map((item, idx) => (
              <div key={idx}>
                <span style={{ fontWeight: 500 }}>{item.label}:</span> {item.value}
              </div>
            ))}
          </div>
        );
      },
    },
    { title: "Sender", render: (_, r) => r.senderName || "-" },
    {
      title: "Sent",
      dataIndex: "createdAt",
      render: (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "-"),
    },
  ];

  return (
    <Modal
      title={<Title level={3}><SendOutlined /> Notification Center – All Sent Messages</Title>}
      open={visible}
      onCancel={onCancel}
      width={1600}
      footer={null}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* ALL FILTERS – EXACTLY AS IN STORY */}
        <Space wrap style={{ gap: 12 }}>
          {/* <Input placeholder="Search title/message..." prefix={<SearchOutlined />} value={searchText} onChange={e => setSearchText(e.target.value)} style={{ width: 260 }} allowClear /> */}
          <RangePicker value={dateRange} onChange={setDateRange} />

          {/* Multi-level: Location → Branch → Designation */}
          {/* <Select placeholder="City" value={cityFilter} onChange={v => { setCityFilter(v); setBranchFilter(""); }} style={{ width: 160 }} allowClear>
            {cities.map(c => <Option key={c}>{c}</Option>)}
          </Select>
          <Select placeholder="Branch" value={branchFilter} onChange={setBranchFilter} style={{ width: 200 }} allowClear disabled={!cityFilter}>
            {branches.map(b => <Option key={b}>{b}</Option>)}
          </Select>
          <Select placeholder="Designation" value={designationFilter} onChange={setDesignationFilter} style={{ width: 200 }} allowClear>
            {designations.map(d => <Option key={d}>{d}</Option>)}
          </Select> */}

          {/* <Select placeholder="Performance" value={performanceFilter} onChange={setPerformanceFilter} style={{ width: 160 }} allowClear>
            <Option value="Top Performer">Top Performer</Option>
            <Option value="Above Average">Above Average</Option>
            <Option value="Needs Improvement">Needs Improvement</Option>
          </Select> */}

          {/* <Select mode="multiple" placeholder="Tenure" value={tenureFilter} onChange={setTenureFilter} style={{ width: 180 }} allowClear>
            {tenureOptions.map(t => <Option key={t}>{t}</Option>)}
          </Select>

          <Select mode="multiple" placeholder="App Usage" value={appUsageFilter} onChange={setAppUsageFilter} style={{ width: 160 }} allowClear>
            {appUsageOptions.map(a => <Option key={a}>{a}</Option>)}
          </Select> */}

          <Select
            mode="multiple"
            placeholder="Cohort Type"
            value={campaignType}
            onChange={setCampaignType}
            style={{ width: 220 }}
            loading={cohortsLoading}
          >
            {cohorts.map(c => (
              <Option key={c.id} value={c.id}>{c.cohortName}</Option>
            ))}
          </Select>

          <Select
            mode="multiple"
            placeholder="Sender"
            value={senderFilter}
            onChange={setSenderFilter}
            style={{ width: 220 }}
            loading={adminsLoading}
          >
            {senders.map((s) => (
              <Option key={s.id} value={s.id}>
                {s.surname}{s.name}
              </Option>
            ))}
          </Select>

          {/* <Select value={readStatus} onChange={setReadStatus} style={{ width: 140 }}>
            <Option value="all">All Status</Option>
            <Option value="read">Mostly Read</Option>
            <Option value="unread">Mostly Unread</Option>
          </Select> */}
        </Space>

        {/* Results */}
        {filteredNotifications.length === 0 ? (
          <Empty description="No notifications match your filters" />
        ) : (
          <Table
            dataSource={filteredNotifications}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1400 }}
          />
        )}
      </Space>
    </Modal>
  );
};

export default NotificationSystem;